// پارسه — the credits screen's vocabulary and arithmetic, shared by both surfaces.
//
// Modelled on doctor-app/parsina.client.web/src/screens/Credits/Credits.shared.js,
// reduced to what a student panel actually spends on: three AI actions, a top-up
// and a gift code. The doctor screen's per-visit KPIs, date-range presets and
// service breakdown are deliberately absent — this app has no visits, and a
// student reading «میانگین هر ویزیت» would be reading about somebody else's job.
//
// ── The unit trap this file exists to hold ────────────────────────────────
//
// پارسه is ONE of three kinds of money on this platform, and the other two are
// unreachable from here: `accounting` holds patient↔clinic money in WHOLE RIALS
// (its `formatMoney` divides by ten), and `subscription` holds what a clinic pays
// Parsina. Never carry a helper between them. Nothing in this file imports the
// kit's `money.*` for exactly that reason — پارسه is a COUNT, not a currency, and
// it is formatted by `fmtParseh` in src/constants/pricing.js.

import { parsehPriceToman } from '../../lib/useCreditPrices.js';
import { ZARINPAL_MIN_TOMAN } from '../../constants/gateway';

// ── The wallet's service vocabulary ────────────────────────────────────────
//
// The ledger's `serviceType` is the WALLET's enum, not this app's. Old rows store
// the proto enum INTEGER as a string ("2", "3", "4") and newer ones the name,
// sometimes prefixed `SERVICE_TYPE_`. All three shapes are normalised to one key
// before anything is looked up.

const SERVICE_TYPE_INT_MAP = { 2: 'AI_TRANSCRIPTION', 3: 'AI_LLM_SUMMARY', 4: 'AI_INTERACTION_CHECK' };

export function serviceKey(rawType) {
  const resolved = SERVICE_TYPE_INT_MAP[rawType] || rawType;
  return String(resolved || '').replace(/^SERVICE_TYPE_/, '');
}

/**
 * Wallet enum → the `credits.service.*` catalogue key.
 *
 * NOTE that a چت question and a تشخیص افتراقی both debit `AI_LLM_SUMMARY`: they
 * are the same RPC with a different prompt, so the LEDGER CANNOT TELL THEM APART
 * and this map must not pretend otherwise. Both rows read «پرسش از دستیار», and
 * the row's own `description` — rendered as the title when it carries one — is
 * what distinguishes them.
 *
 * Total: an enum this app has not heard of becomes «قابلیت هوشمند» rather than a
 * raw wallet constant, because the reader of this screen is a student and
 * `AI_SOMETHING_NEW` is not a sentence.
 */
export const SERVICE_LABEL_KEYS = {
  AI_LLM_SUMMARY: 'credits.service.ask',
  AI_INTERACTION_CHECK: 'credits.service.interaction',
  AI_TRANSCRIPTION: 'credits.service.transcription',
  GIFT_CODE: 'credits.service.gift',
  TOP_UP: 'credits.service.topup',
  TOPUP: 'credits.service.topup',
  SMS: 'credits.service.sms',
};

export const DEFAULT_SERVICE_KEY = 'credits.service.other';

export const serviceLabelKey = (rawType) =>
  SERVICE_LABEL_KEYS[serviceKey(rawType)] || DEFAULT_SERVICE_KEY;

/**
 * The description the wallet stored, as something to read.
 *
 * The AI service writes a JSON blob there (`action`, token counts, the model
 * name). The token counts and the model are DELIBERATELY not shown: they are the
 * platform's unit economics, not the student's, and «۳۲٬۰۰۰ توکن · gemini-2.5-pro»
 * is a line that invites a question about which model ran rather than about what
 * the answer cost. A plain-string description is passed through as written.
 */
export function transactionTitle(raw) {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return String(parsed.action || '');
    return String(parsed || '');
  } catch {
    return String(raw);
  }
}

// ── Top-up arithmetic ──────────────────────────────────────────────────────

/**
 * A package size in پارسه → what it COSTS, in Toman.
 *
 * `PARSEH_PRICE_TOMAN` is what this app CHARGES per پارسه, and printing it beside
 * a package is the honest thing to do. It is deliberately NOT used to predict the
 * credited balance: the wallet's top-up path currently credits 1 Toman = 1 پارسه
 * with no conversion (a known server placeholder), so a student who pays 500,000
 * Toman is credited 500,000 پارسه rather than 500. The screen therefore shows the
 * balance the wallet REPORTS after the callback settles, never an arithmetic
 * guess. When the wallet's conversion is fixed the two finally agree and nothing
 * here changes.
 */
export const packageToman = (parseh) => Math.round(Number(parseh || 0) * parsehPriceToman());

/**
 * The floor, in Toman, for any purchase.
 *
 * It is ZARINPAL's own documented minimum, applied to BOTH gateways: it is a
 * property of that gateway rather than of this app, and a sub-1000-Toman top-up
 * is meaningless in either gateway's unit anyway. Applying it unconditionally
 * means switching `PAYMENT_GATEWAY` cannot silently drop the check.
 */
export const MIN_TOPUP_TOMAN = ZARINPAL_MIN_TOMAN;

/** A typed Toman amount → the digits, or NaN. Persian digits are folded first. */
export function parseTomanInput(value) {
  const folded = String(value ?? '')
    // Persian and Arabic-Indic digits, so a student typing on a Persian keyboard
    // is not told their amount is invalid. The BFF is not asked to guess.
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))) // i18n-exempt: digit folding, not copy
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d))) // i18n-exempt: digit folding, not copy
    .replace(/[^\d]/g, '');
  return folded ? Number(folded) : NaN;
}

/** `credits.gift.status.*` — the four states, computed or stored. */
export const GIFT_STATUSES = ['issued', 'redeemed', 'revoked', 'expired'];
export const giftStatusKey = (status) =>
  `credits.gift.status.${GIFT_STATUSES.includes(status) ? status : 'issued'}`;

export const giftStatusVariant = (status) => {
  if (status === 'redeemed') return 'success';
  if (status === 'revoked' || status === 'expired') return 'outline';
  return 'warning';
};

/** `credits.tx.filter.*` — the ledger filter, and the wire value it sends. */
export const TX_FILTERS = [
  { id: 'all', value: null, labelKey: 'credits.tx.filter.all' },
  { id: 'credit', value: 'credit', labelKey: 'credits.tx.filter.credit' },
  { id: 'debit', value: 'debit', labelKey: 'credits.tx.filter.debit' },
];
